import React from 'react';

import type { VariantProp } from '../../common/colors';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import { selectors, transitions } from '../../controls/shared/styles';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';

const LinkContainer = styled('a', Text, {
  position: 'relative',
  color: '$$linkInitial',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: transitions.control,

  [selectors.hover]: {
    color: '$$linkInteractive',
    textDecoration: 'underline',
  },

  [selectors.focus]: {
    color: '$$linkInteractive',
    outline: 'none',

    '&::before': {
      content: '',
      position: 'absolute',
      inset: '-$2 -$4',
      display: 'flex',
      boxShadow: shadows.focusRingLight,
      borderRadius: '$6',

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },

  variants: {
    variant: {
      alternative: {
        $$linkInitial: colors.linkAlternativeInitialLight,
        $$linkInteractive: colors.linkAlternativeInteractiveLight,

        [darkThemeSelector]: {
          $$linkInitial: colors.linkAlternativeInitialDark,
          $$linkInteractive: colors.linkAlternativeInteractiveDark,
        },
      },
      brand: {
        $$linkInitial: colors.linkBrandInitialLight,
        $$linkInteractive: colors.linkBrandInteractiveLight,

        [darkThemeSelector]: {
          $$linkInitial: colors.linkBrandInitialDark,
          $$linkInteractive: colors.linkBrandInteractiveDark,
        },
      },
      attention: {
        $$linkInitial: colors.linkAttentionInitialLight,
        $$linkInteractive: colors.linkAttentionInteractiveLight,

        [darkThemeSelector]: {
          $$linkInitial: colors.linkAttentionInitialDark,
          $$linkInteractive: colors.linkAttentionInteractiveDark,
        },
      },
      negative: {
        $$linkInitial: colors.linkNegativeInitialLight,
        $$linkInteractive: colors.linkNegativeInteractiveLight,

        [darkThemeSelector]: {
          $$linkInitial: colors.linkNegativeInitialDark,
          $$linkInteractive: colors.linkNegativeInteractiveDark,
        },
      },
      neutral: {
        $$linkInitial: colors.linkNeutralInitialLight,
        $$linkInteractive: colors.linkNeutralInteractiveLight,

        [darkThemeSelector]: {
          $$linkInitial: colors.linkNeutralInitialDark,
          $$linkInteractive: colors.linkNeutralInteractiveDark,
        },
      },
      positive: {
        $$linkInitial: colors.linkPositiveInitialLight,
        $$linkInteractive: colors.linkPositiveInteractiveLight,

        [darkThemeSelector]: {
          $$linkInitial: colors.linkPositiveInitialDark,
          $$linkInteractive: colors.linkPositiveInteractiveDark,
        },
      },
    },
  },
});
LinkContainer.displayName = 'LinkContainer';

export interface LinkProps {
  /**
   * The variant of the link.
   */
  variant?: VariantProp;
}

function LinkInner<Tag extends React.ElementType>(
  { children, variant = 'alternative', ...props }: PolymorphicComponentProps<Tag, LinkProps>,
  ref: PolymorphicRef<Tag>,
) {
  return (
    <LinkContainer tabIndex={0} variant={variant} {...props} ref={ref}>
      {children}
    </LinkContainer>
  );
}

type PropsOf<T extends React.ElementType> = React.ComponentPropsWithRef<T>;

type PolymorphicRef<T extends React.ElementType> = React.ComponentPropsWithRef<T>['ref'];

type PolymorphicProps<T extends React.ElementType = React.ElementType, TProps = {}> = {
  as?: T;
} & TProps &
  Omit<PropsOf<T>, keyof TProps | 'as' | 'ref'> & { ref?: PolymorphicRef<T> };

export const Link = React.forwardRef(LinkInner) as <T extends React.ElementType = 'a'>(
  props: PolymorphicProps<T, LinkProps>,
) => React.JSX.Element | null;
